import mqtt, { AsyncMqttClient, IClientPublishOptions, QoS } from 'async-mqtt';
import retry from 'async-retry';
import { isEmpty, uniq, without } from 'lodash';
import path from 'path';

import Configs, { IBrokerFile } from './configs';
import Logger from './logger';
import { loadJSONFile } from './utils';

export const singletonEnforcer = Symbol('singletonEnforcer');

export type IMqttCallback<T = void> = (topic?: string, message?: any) => Promise<T>;
// export type IMqttAliveCallback<T = Record<string, unknown>> = () => T;
export type IListener = {
  callback: IMqttCallback;
  topics?: string[];
  custom: boolean;
}

export default class Communication {
  public static connect(): Promise<void> {
    return Communication.instance.connect();
  }

  public static disconnect(): Promise<void> {
    return Communication.instance.disconnect();
  }

  public static subscribe(topics: string | string[]): Promise<void> {
    return Communication.instance.subscribe(topics);
  }

  public static unsubscribe(topics: string | string[]): Promise<void> {
    return Communication.instance.unsubscribe(topics);
  }

  public static customSubscribe(topics: string | string[]): Promise<void> {
    return Communication.instance.customSubscribe(topics);
  }

  public static customUnsubscribe(topics: string | string[]): Promise<void> {
    return Communication.instance.customUnsubscribe(topics);
  }

  public static addListener(name: string, topics: string | string[] | void = [], callback: IMqttCallback): void {
    Communication.instance.addListener(name, topics, callback);
  }

  public static addCustomListener(name: string, topics: string | string[] | void = [], callback: IMqttCallback): void {
    Communication.instance.addCustomListener(name, topics, callback);
  }

  public static removeListener(name: string): void {
    Communication.instance.removeListener(name);
  }

  public static directPublish(topic: string, message: string | Buffer | object = {}, options?: IClientPublishOptions): Promise<void> {
    return Communication.instance.directPublish(topic, message, options);
  }

  public static publish(destination: string, action: string, message: string | Buffer | object = {}, options?: IClientPublishOptions): Promise<void> {
    return Communication.instance.publish(destination, action, message, options);
  }

  public static messageTo(destination: string, action: string, message: string | Buffer | object = {}, options?: IClientPublishOptions): Promise<void> {
    return Communication.instance.publish(destination, action, message, options);
  }

  public static systemPublish(action: string, message: string | Buffer | object = {}, options?: IClientPublishOptions): Promise<void> {
    return Communication.instance.systemPublish(action, message, options);
  }

  public static systemMessage(action: string, message: string | Buffer | object = {}, options?: IClientPublishOptions): Promise<void> {
    return Communication.instance.systemMessage(action, message, options);
  }

  public static isConnected(): boolean {
    return Communication.instance.isConnected();
  }

  public static destroy(): void {
    Communication.singleton = undefined;
  }

  public static buildTopic(topic: string): string {
    const { app } = Configs.configs;
    // if topic already matches the full format just subscribe to it
    // topic format: [source]/[type]/[app.name]/[action]
    // eslint-disable-next-line no-useless-escape
    const regExp = new RegExp(`^\\w+/\\w+/${app.name}/\\w+$`, 'g');
    if (regExp.test(topic)) {
      return topic;
    }
    // match any message sent from any source to current project with action == topic
    return path.join('+', Communication.TYPE_MSG, app.name, `${topic}`);
  }

  public static buildTopics(topics: string | string[] | void): string[] {
    const builtTopics: string[] = [];
    (Array.isArray(topics) ? topics : [topics]).forEach((topic) => {
      if (topic) {
        builtTopics.push(Communication.buildTopic(topic));
      }
    });
    return builtTopics;
  }

  public static topicFor(destination: string, action: string): string {
    // topic format: [app.name]/[type]/[destination]/[action]
    const { app } = Configs.configs;
    return path.join(app.name, Communication.TYPE_MSG, destination, action);
  }

  public static topicForCore(action: string): string {
    return Communication.topicFor(Communication.CORE_PREFIX, action);
  }

  public static isTopicSubscribed(topics: string[], msgTopic: string): boolean {
    const { app } = Configs.configs;
    const [, , destination, action] = msgTopic.split('/');
    // if destinated to me, discard
    if (destination !== app.name) {
      return false;
    }
    const actions = topics.map((topic) => {
      const [, , , act] = topic.split('/');
      return act;
    });
    // keep  if one of the subscribed topic ha a wilcard action
    // keep if the action matches exactly one of the subscribed topic
    return actions.includes('#') || actions.includes('+') || actions.includes(action);
  }

  private static TYPE_MSG = 'msg';

  private static CORE_PREFIX = 'core';

  private static DEFAULT_QOS: QoS = 2;

  private static CONNECTION_RETRY_TIMEOUT = 5000;

  private client: AsyncMqttClient;

  private listeners: { [name: string]: IListener } = {};

  private topics: string[] = [];

  protected static singleton: Communication;

  constructor(enforcer: symbol) {
    if (enforcer !== singletonEnforcer) { throw new Error('Cannot construct singleton'); }
  }

  public async connect(): Promise<void> {
    try {
      await retry(async () => {
        const { communication, app } = Configs.configs;
        const { host, port, options } = communication;
        const clientId = `${app.name}_${Math.random().toString(16).substr(2, 8)}`;
        let username = process.env.BROKER_USERNAME;
        let password = process.env.BROKER_PASSWORD;
        if (isEmpty(username) || isEmpty(password)) {
          ({ username, password } = loadJSONFile(app.brokerFilePath) as IBrokerFile);
        }
        this.client = await mqtt.connectAsync({
          host,
          port,
          username,
          password,
          clientId,
          keepalive: 5,
          connectTimeout: 5000,
          ...options });
        Logger.info(`Client connected to broker: ${host}:${port}`);
        // subscribe default topics
        this.client.on('message', (msgTopic: string, message: Buffer) => {
          let msg: Buffer | object | string = {};
          try {
            msg = JSON.parse(message.toString());
          } catch (e) {
            msg = message;
          }
          Object.entries(this.listeners).forEach(([name, listener]) => {
            const { callback, topics, custom } = listener;
            if (topics.length === 0
              || Communication.isTopicSubscribed(topics, msgTopic)
              || (custom && topics.includes(msgTopic))) {
              Logger.silly(`Received message for topic ${msgTopic}`, msg);
              callback(msgTopic, msg);
            } else {
              Logger.silly(`Listener ${name} not interested in topic ${msgTopic}`);
            }
          });
        });
        this.client.on('error', (e: Error) => { Logger.error(e.message); });
      }, {
        minTimeout: Communication.CONNECTION_RETRY_TIMEOUT,
        maxTimeout: Communication.CONNECTION_RETRY_TIMEOUT,
        forever: true,
        onRetry: (e: Error) => { Logger.error(e.message); }
      });
    } catch (e) {
      Logger.error(e);
    }
  }

  public async disconnect(): Promise<void> {
    if (this.client) {
      await this.unsubscribe();
      await this.client.end();
      Logger.info('Client disconnected from broker');
    }
  }

  public addListener(name: string, topics: string | string[] | void = [], callback: IMqttCallback): void {
    if (!isEmpty(topics)) {
      this.listeners[name] = { callback, topics: Communication.buildTopics(topics), custom: false };
    }
  }

  public addCustomListener(name: string, topics: string | string[] | void = [], callback: IMqttCallback): void {
    if (topics && !isEmpty(topics)) {
      this.listeners[name] = { callback, topics: Array.isArray(topics) ? topics : [topics], custom: true };
    }
  }

  public removeListener(name: string): void {
    delete this.listeners[name];
  }

  public async customSubscribe(topics: string | string[]): Promise<void> {
    if (this.isConnected()) {
      await this.client.subscribe(topics, { qos: Communication.DEFAULT_QOS });
      this.topics.push(...topics);
      this.topics = uniq(this.topics);
      Logger.debug('Client subscribed to topics', topics);
    }
  }

  public async subscribe(topics: string | string[]): Promise<void> {
    if (this.isConnected()) {
      const fullTopics = Communication.buildTopics(topics);
      await this.client.subscribe(fullTopics, { qos: Communication.DEFAULT_QOS });
      this.topics.push(...fullTopics);
      this.topics = uniq(this.topics);
      Logger.debug('Client subscribed to topics', fullTopics);
    }
  }

  public async unsubscribe(topics?: string | string[]): Promise<void> {
    if (this.isConnected()) {
      let topicsToUnsubscribe = [];
      if (topics) {
        topicsToUnsubscribe = Communication.buildTopics(topics);
        await this.client.unsubscribe(topicsToUnsubscribe);
        this.topics = without(this.topics, ...(Array.isArray(topicsToUnsubscribe) ? topicsToUnsubscribe : [topicsToUnsubscribe]));
      } else {
        topicsToUnsubscribe = this.topics;
        if (Object.keys(this.topics).length > 0) await this.client.unsubscribe(Object.keys(this.topics));
        this.topics = [];
      }
      this.topics = uniq(this.topics);
      Logger.debug('Client unsubscribed from topics', topicsToUnsubscribe);
    }
  }

  public async customUnsubscribe(topics?: string | string[]): Promise<void> {
    if (this.isConnected()) {
      if (topics) {
        await this.client.unsubscribe(topics);
        this.topics = without(this.topics, ...(Array.isArray(topics) ? topics : [topics]));
        this.topics = uniq(this.topics);
        Logger.debug('Client unsubscribed from topics', topics);
      } else {
        Logger.warn('Missing topics for custom unsubscribe');
      }
    }
  }

  public async messageTo(destination: string, action: string, message: string | Buffer | object = {}, options?: IClientPublishOptions): Promise<void> {
    return this.publish(destination, action, message, options);
  }

  public async systemMessage(action: string, message: string | Buffer | object = {}, options?: IClientPublishOptions): Promise<void> {
    return this.publish(Communication.CORE_PREFIX, action, message, options);
  }

  public async systemPublish(action: string, message: string | Buffer | object = {}, options?: IClientPublishOptions): Promise<void> {
    return this.publish(Communication.CORE_PREFIX, action, message, options);
  }

  public async publish(destination: string, action: string, message: string | Buffer | object = {}, options: IClientPublishOptions = { qos: Communication.DEFAULT_QOS }): Promise<void> {
    if (this.isConnected()) {
      const builtTopic = Communication.topicFor(destination, action);
      Logger.silly(`Sending message to topic ${builtTopic}`, message);
      const { retain = false, qos } = options;
      const content: string | Buffer = (Buffer.isBuffer(message)) ? message : JSON.stringify(message);
      await this.client.publish(builtTopic, content, { retain, qos });
    }
  }

  public async directPublish(topic: string, message: string | Buffer | object = {}, options: IClientPublishOptions = { qos: Communication.DEFAULT_QOS }): Promise<void> {
    if (this.isConnected()) {
      Logger.silly(`Sending message to topic ${topic}`, message);
      const { retain = false, qos } = options;
      const content: string | Buffer = (Buffer.isBuffer(message)) ? message : JSON.stringify(message);
      await this.client.publish(topic, content, { retain, qos });
    }
  }

  public isConnected(): boolean {
    return (this.client && this.client.connected);
  }

  static get instance(): Communication {
    if (!Communication.singleton) {
      Communication.singleton = new Communication(singletonEnforcer);
    }
    return Communication.singleton;
  }

  static set instance(instance: Communication) {
    Communication.singleton = instance;
  }

  static get client(): AsyncMqttClient {
    return Communication.instance.client;
  }
}
